(function() {
  "use strict";
  const HIDDEN_FOCUS_SELECTOR = "[data-patrol-hidden-focus]";
  function syncInertWithAriaHidden(element) {
    element.toggleAttribute(
      "inert",
      element.getAttribute("aria-hidden") === "true"
    );
  }
  function startHiddenFocusSync() {
    const syncWithin = (root) => {
      if (root instanceof HTMLElement && root.matches(HIDDEN_FOCUS_SELECTOR)) {
        syncInertWithAriaHidden(root);
      }
      for (const element of root.querySelectorAll(
        HIDDEN_FOCUS_SELECTOR
      )) {
        syncInertWithAriaHidden(element);
      }
    };
    syncWithin(document.documentElement);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          const target = mutation.target;
          if (target instanceof HTMLElement && target.matches(HIDDEN_FOCUS_SELECTOR)) {
            syncInertWithAriaHidden(target);
          }
          continue;
        }
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            syncWithin(node);
          }
        }
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-hidden"]
    });
  }
  const hiddenFocusSyncFix = {
    id: "hidden-focus",
    start: startHiddenFocusSync
  };
  function watchMarkedElements(selector, managedAttribute, attach) {
    const attachOnce = (element) => {
      if (element instanceof HTMLElement && !element.hasAttribute(managedAttribute)) {
        element.setAttribute(managedAttribute, "");
        attach(element);
      }
    };
    const attachWithin = (root) => {
      if (root.matches(selector)) {
        attachOnce(root);
      }
      for (const element of root.querySelectorAll(selector)) {
        attachOnce(element);
      }
    };
    attachWithin(document.documentElement);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            attachWithin(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
  const FOCUSABLE_SELECTOR = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]';
  const ESCAPE_CLOSE_WAIT_MS = 150;
  function isPopupVisible(popup) {
    if (popup.hasAttribute("hidden")) {
      return false;
    }
    if (popup.getAttribute("aria-hidden") === "true") {
      return false;
    }
    if (popup instanceof HTMLDialogElement && !popup.open) {
      return false;
    }
    const style = window.getComputedStyle(popup);
    return style.display !== "none" && style.visibility !== "hidden" && parseFloat(style.opacity) !== 0;
  }
  function getVisibleFocusableElements(popup) {
    const focusables = popup.querySelectorAll(FOCUSABLE_SELECTOR);
    return Array.from(focusables).filter(
      (element) => element.getClientRects().length > 0
    );
  }
  function findVisibleCloseControl(popup) {
    const controls = popup.querySelectorAll(
      '[data-close], button[class*="close" i], [aria-label*="close" i]'
    );
    for (const control of controls) {
      if (control.getClientRects().length > 0) {
        return control;
      }
    }
    return null;
  }
  function attachPopupFocusManagement(popup) {
    let isOpen = isPopupVisible(popup);
    let triggerElement = null;
    const handleKeydown = (event) => {
      // Listener lives on document; ignore keys once this popup is no longer the open one.
      if (!isOpen) {
        return;
      }
      if (!popup.isConnected) {
        handleClose();
        return;
      }
      if (event.key === "Tab") {
        const focusables = getVisibleFocusableElements(popup);
        if (focusables.length === 0) {
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (!popup.contains(active)) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        }
      } else if (event.key === "Escape") {
        window.setTimeout(() => {
          var _a;
          if (!popup.isConnected || !isPopupVisible(popup)) {
            return;
          }
          if (popup instanceof HTMLDialogElement) {
            popup.close();
            return;
          }
          (_a = findVisibleCloseControl(popup)) == null ? void 0 : _a.click();
        }, ESCAPE_CLOSE_WAIT_MS);
      }
    };
    const handleOpen = () => {
      const active = document.activeElement;
      triggerElement = active instanceof HTMLElement && !popup.contains(active) ? active : null;
      if (!popup.contains(active)) {
        const focusables = getVisibleFocusableElements(popup);
        if (focusables.length > 0) {
          focusables[0].focus();
        } else {
          if (!popup.hasAttribute("tabindex")) {
            popup.setAttribute("tabindex", "-1");
          }
          popup.focus();
        }
      }
      document.addEventListener("keydown", handleKeydown);
    };
    const handleClose = () => {
      document.removeEventListener("keydown", handleKeydown);
      if (triggerElement == null ? void 0 : triggerElement.isConnected) {
        triggerElement.focus();
      }
      triggerElement = null;
    };
    const observer = new MutationObserver(() => {
      const isVisible = isPopupVisible(popup);
      if (isVisible === isOpen) {
        return;
      }
      isOpen = isVisible;
      if (isVisible) {
        handleOpen();
      } else {
        handleClose();
      }
    });
    observer.observe(popup, {
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden", "open", "hidden"]
    });
  }
  const popupFocusManagementFix = {
    id: "pop-up",
    start: () => watchMarkedElements(
      "[data-patrol-pop-up]",
      "data-patrol-pop-up-managed",
      attachPopupFocusManagement
    )
  };
  const TITLE_PROMOTION_MARKER = "⁠";
  function attachTitlePromotion(element) {
    const title = element.getAttribute("title");
    if (title === null || title[0] !== TITLE_PROMOTION_MARKER) {
      return;
    }
    element.setAttribute("aria-label", title.slice(1));
    element.removeAttribute("title");
  }
  const titlePromotionFix = {
    id: "title-promotion",
    start: () => watchMarkedElements(
      `a[title^="${TITLE_PROMOTION_MARKER}"]`,
      "data-patrol-title-managed",
      attachTitlePromotion
    )
  };
  const MARKER_FIXES = [
    hiddenFocusSyncFix,
    popupFocusManagementFix,
    titlePromotionFix
  ];
  function startMarkerFixRuntime() {
    for (const fix of MARKER_FIXES) {
      fix.start();
    }
  }
  startMarkerFixRuntime();
})();
